import React, { FC, useEffect, useMemo, useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import type { Media } from '../imagePreview/media';

type LocalItem = { kind: 'local'; file: File; objectUrl: string };
type ServerItem = { kind: 'server'; media: Media };
type MediaItem = LocalItem | ServerItem;

function isVideoFile(file: File): boolean {
  return file.type.startsWith('video/');
}

function isVideoUrl(item: ServerItem): boolean {
  return (item.media.content_type?.startsWith('video/') ?? false) || /\.(mp4|webm|ogg)$/i.test(item.media.url);
}

interface MediaUploadSectionProps {
  label: string;
  files: File[];
  onChange: (files: File[]) => void;
  existingMedia?: Media[];
  onRemoveExisting?: (id: number | string) => void;
  // Some flows are photo-only (warehouse receiving); video stays on by default for the
  // inspection-report sections that rely on it.
  allowVideo?: boolean;
  required?: boolean;
  error?: boolean;
}

const MediaUploadSection: FC<MediaUploadSectionProps> = ({
  label,
  files,
  onChange,
  existingMedia = [],
  onRemoveExisting,
  allowVideo = true,
  required,
  error,
}) => {
  const [galleryOpen, setGalleryOpen] = useState(false);

  const localItems: LocalItem[] = useMemo(
    () => files.map((file) => ({ kind: 'local' as const, file, objectUrl: URL.createObjectURL(file) })),
    [files],
  );

  useEffect(() => {
    return () => {
      localItems.forEach((item) => URL.revokeObjectURL(item.objectUrl));
    };
  }, [localItems]);

  const serverItems: ServerItem[] = useMemo(
    () => existingMedia.map((media) => ({ kind: 'server' as const, media })),
    [existingMedia],
  );

  const allItems: MediaItem[] = useMemo(() => [...serverItems, ...localItems], [serverItems, localItems]);
  const totalCount = allItems.length;
  const hasMultiple = totalCount >= 3;
  const displayItems = hasMultiple ? allItems.slice(0, 2) : allItems;

  const handleAddFiles = (newFiles: FileList | null) => {
    if (!newFiles || newFiles.length === 0) return;
    onChange([...files, ...Array.from(newFiles)]);
  };

  const handleRemoveLocal = (index: number) => {
    const serverCount = serverItems.length;
    const localIndex = index - serverCount;
    onChange(files.filter((_, i) => i !== localIndex));
  };

  const handleRemoveItem = (index: number) => {
    const item = allItems[index];
    if (item.kind === 'server' && onRemoveExisting && item.media.id != null) {
      onRemoveExisting(item.media.id);
    } else {
      handleRemoveLocal(index);
    }
  };

  const renderThumbnail = (item: MediaItem, index: number) => {
    const isVideo = item.kind === 'local' ? isVideoFile(item.file) : isVideoUrl(item);
    const url = item.kind === 'local' ? item.objectUrl : item.media.url;
    const altText = `${label} ${index + 1}`;

    return (
      <Grid item xs={12} sm={6} md={hasMultiple ? 6 : 4} key={`media-${index}`}>
        <Card
          sx={{
            position: 'relative',
            height: '100%',
            transition: 'all 0.3s ease',
            cursor: 'default',
            '&:hover': { boxShadow: 4, transform: 'translateY(-4px)' },
          }}
        >
          {isVideo ? (
            <Box
              sx={{
                width: '100%',
                height: 200,
                bgcolor: 'grey.900',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PlayArrowIcon sx={{ fontSize: 72, color: 'white', opacity: 0.9 }} />
            </Box>
          ) : (
            <CardMedia component="img" image={url} alt={altText} sx={{ height: 200, objectFit: 'cover' }} />
          )}
          <IconButton
            size="small"
            onClick={() => handleRemoveItem(index)}
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              bgcolor: 'rgba(0,0,0,0.6)',
              color: 'white',
              '&:hover': { bgcolor: 'error.main' },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Card>
      </Grid>
    );
  };

  return (
    <>
      <Card sx={{ mb: 3, boxShadow: 2 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <PhotoLibraryIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {label}
              {required && (
                <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>
                  *
                </Box>
              )}
            </Typography>
            {totalCount > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                {totalCount} {totalCount === 1 ? 'item' : 'items'}
              </Typography>
            )}
          </Stack>

          <Divider sx={{ mb: 2 }} />

          <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 3 }}>
            <Button variant="contained" component="label" size="medium">
              Take Photo
              <input
                hidden
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => {
                  handleAddFiles((e.target as HTMLInputElement).files);
                  e.target.value = '';
                }}
              />
            </Button>

            {allowVideo && (
              <Button variant="contained" color="success" component="label" size="medium">
                Take Video
                <input
                  hidden
                  type="file"
                  accept="video/*"
                  capture="environment"
                  onChange={(e) => {
                    handleAddFiles((e.target as HTMLInputElement).files);
                    e.target.value = '';
                  }}
                />
              </Button>
            )}

            <Button variant="contained" color="secondary" component="label" size="medium">
              Gallery
              <input
                hidden
                type="file"
                accept={allowVideo ? 'image/*,video/*' : 'image/*'}
                multiple
                onChange={(e) => {
                  handleAddFiles((e.target as HTMLInputElement).files);
                  e.target.value = '';
                }}
              />
            </Button>
          </Stack>

          {totalCount === 0 ? (
            <Box
              sx={{
                py: 4,
                textAlign: 'center',
                bgcolor: 'grey.50',
                borderRadius: 1,
                border: '2px dashed',
                borderColor: error ? 'error.main' : 'grey.300',
              }}
            >
              <PhotoLibraryIcon sx={{ fontSize: 48, color: error ? 'error.main' : 'grey.400', mb: 1 }} />
              <Typography variant="body2" color={error ? 'error' : 'text.secondary'}>
                No images uploaded yet
              </Typography>
              {error && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                  This field is required
                </Typography>
              )}
            </Box>
          ) : (
            <>
              <Grid container spacing={2}>
                {displayItems.map((item, index) => renderThumbnail(item, index))}
              </Grid>

              {hasMultiple && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Button
                    variant="outlined"
                    onClick={() => setGalleryOpen(true)}
                    startIcon={<PhotoLibraryIcon />}
                    size="large"
                  >
                    View All {totalCount} Items
                  </Button>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={galleryOpen} onClose={() => setGalleryOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {label} - All Items
          <IconButton
            aria-label="close"
            onClick={() => setGalleryOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            {allItems.map((item, index) => {
              const isVideo = item.kind === 'local' ? isVideoFile(item.file) : isVideoUrl(item);
              const url = item.kind === 'local' ? item.objectUrl : item.media.url;

              return (
                <Grid item xs={12} sm={6} md={4} key={`gallery-${index}`}>
                  <Card sx={{ position: 'relative' }}>
                    {isVideo ? (
                      <Box
                        sx={{
                          width: '100%',
                          height: 200,
                          bgcolor: 'grey.900',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <PlayArrowIcon sx={{ fontSize: 72, color: 'white', opacity: 0.9 }} />
                      </Box>
                    ) : (
                      <CardMedia
                        component="img"
                        image={url}
                        alt={`${label} ${index + 1}`}
                        sx={{ height: 200, objectFit: 'cover' }}
                      />
                    )}
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveItem(index)}
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        bgcolor: 'rgba(0,0,0,0.6)',
                        color: 'white',
                        '&:hover': { bgcolor: 'error.main' },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MediaUploadSection;
